/**
 * MappingTable — step 3 (Map). An on-brand wrapper around the shared
 * ThingTagRows.TagRowsEditor, seeded from the registers ticked on the previous
 * step. No new row-editor code: the same editor the Add-a-Thing wizard uses,
 * so register->tag mapping looks and behaves identically to the rest of the app.
 * The Modbus wire metadata (address / reg_count / p_type / register space / scale)
 * rides silently inside each row and round-trips into the createInstrument param map
 * at go-live.
 *
 * Rows are compact (name, address, type on one line) with the full editor
 * behind each row's expand; an unnamed or unaddressed row starts open.
 *
 * "Load register map" imports a mock/plant register_map.json for the discovered unit,
 * so an operator does not hand-name hr_N: each register arrives with its real metric
 * name (the operating_modes tag_hint), register space (coil onboards as boolean_state)
 * and scale.
 */

import { useMemo, useRef, useState, type ChangeEvent } from "react";
import { Upload, Wrench } from "lucide-react";
import { Button } from "@/lib/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { TagRowsEditor, type TagRow } from "@/components/iot/ThingTagRows";
import type { DriverInfo } from "@/lib/api";
import { type RegisterMap } from "@/components/connect/commit";

export interface MappingTableProps {
  rows: TagRow[];
  onAddRow: () => void;
  onRemoveRow: (key: string) => void;
  onPatchRow: (key: string, patch: Partial<TagRow>) => void;
  /** Import a mock/plant register_map.json to seed the rows for the discovered unit
   *  (kind / scale / data_kind + verbatim metric names). Omit to hide the button. */
  onLoadRegisterMap?: (map: RegisterMap) => void;
  /** A non-Modbus device's driver: rows edit its point address and type instead of Modbus registers. */
  pointDriver?: DriverInfo;
}

export function MappingTable({
  rows,
  onAddRow,
  onRemoveRow,
  onPatchRow,
  onLoadRegisterMap,
  pointDriver,
}: MappingTableProps) {
  // Autocomplete pools sourced from the rows themselves (register names + units),
  // so renaming one tag suggests the vocabulary already in the map.
  const tagNameOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.name.trim()).filter(Boolean))),
    [rows],
  );
  const unitOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.unit.trim()).filter(Boolean))),
    [rows],
  );
  const named = rows.filter((r) => r.name.trim()).length;

  const fileRef = useRef<HTMLInputElement>(null);
  const [importErr, setImportErr] = useState<string | null>(null);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f || !onLoadRegisterMap) return;
    try {
      const map = JSON.parse(await f.text()) as RegisterMap;
      if (!map || typeof map !== "object" || !map.units) {
        setImportErr('Not a register_map.json (no "units").');
        return;
      }
      setImportErr(null);
      onLoadRegisterMap(map);
    } catch {
      setImportErr("Could not parse that file as JSON.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wrench className="size-5 text-brand" />
              {pointDriver ? "Map points to tags" : "Map registers to tags"}
            </CardTitle>
            <CardDescription>
              Name each {pointDriver ? "point" : "register"}; these become the device&apos;s tags. Open a row for
              unit, poll rate and address. {named} of {rows.length} named.
            </CardDescription>
          </div>
          {onLoadRegisterMap && !pointDriver && (
            <div className="shrink-0 text-right">
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={onFile}
              />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="size-4" />
                Load register map
              </Button>
              {importErr && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{importErr}</p>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <TagRowsEditor
          rows={rows}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveRow}
          onPatchRow={onPatchRow}
          thingSimulate={false}
          tagNameOptions={tagNameOptions}
          unitOptions={unitOptions}
          showModbus
          pointDriver={pointDriver}
          compact
        />
      </CardContent>
    </Card>
  );
}
